/**
 * Time-based alert helpers for Unscheduled and Planned event homepages.
 *
 * A time field (Next Advise or ERTS/RTS) is:
 *  - 'overdue' (red)   when its value is earlier than now.
 *  - 'warning' (yellow) when its value is within the warning threshold of now.
 *  - null               otherwise (or when empty/unparseable).
 *
 * The aggregate state across a set of events is the most severe field state
 * found, with 'overdue' taking precedence over 'warning'.
 */
import { UnscheduledEvent } from '../types';
import { parseFlexibleDate } from './dateUtils';
import { TIME_ALERT_WARNING_THRESHOLD_MINUTES } from '../constants';

export type TimeAlertState = 'overdue' | 'warning' | null;

const MILLISECONDS_PER_MINUTE = 1000 * 60;

/**
 * Determines the alert state for a single datetime value relative to `now`.
 */
export function getTimeFieldAlertState(value: string, now: Date = new Date()): TimeAlertState {
  const target = parseFlexibleDate(value);
  if (!target) return null;
  const diffMs = target.getTime() - now.getTime();
  if (diffMs < 0) return 'overdue';
  if (diffMs <= TIME_ALERT_WARNING_THRESHOLD_MINUTES * MILLISECONDS_PER_MINUTE) return 'warning';
  return null;
}

/**
 * Returns the more severe of two alert states ('overdue' > 'warning' > null).
 */
export function mergeTimeAlertStates(a: TimeAlertState, b: TimeAlertState): TimeAlertState {
  if (a === 'overdue' || b === 'overdue') return 'overdue';
  if (a === 'warning' || b === 'warning') return 'warning';
  return null;
}

/**
 * Computes the most severe alert state for a single event across its
 * Next Advise and ERTS/RTS times.
 */
export function getEventTimeAlertState(
  event: UnscheduledEvent,
  now: Date = new Date(),
): TimeAlertState {
  const nextAdvise = getTimeFieldAlertState(event.nextAdviseTimeZulu, now);
  const erts = getTimeFieldAlertState(event.returnToServiceTimeZulu, now);
  return mergeTimeAlertStates(nextAdvise, erts);
}

/**
 * Computes the most severe alert state across a list of events.
 */
export function getEventsTimeAlertState(
  events: UnscheduledEvent[],
  now: Date = new Date(),
): TimeAlertState {
  let state: TimeAlertState = null;
  for (const event of events) {
    state = mergeTimeAlertStates(state, getEventTimeAlertState(event, now));
    if (state === 'overdue') break;
  }
  return state;
}
